<template>
  <div class="payment-subaccount-config-form">
    <form @submit.prevent>
      <div class="info-banner">
        <i class="pi pi-info-circle" />
        <span>{{ t('Configure as preferências de pagamento e notificações para esta subconta.') }}</span>
      </div>

      <!-- Payment Settings -->
      <div class="config-section">
        <h4>{{ t('Configurações de Pagamento') }}</h4>

        <div class="field">
          <select-list
            data-testid="subaccounts-config-default-payment-method-select"
            v-model="formData.default_payment_method"
            elem-id="subaccounts-config-default-payment-method"
            :label="t('Método de Pagamento Padrão')"
            :placeholder="t('Selecione')"
            :itens-select="paymentMethods"
          />
        </div>

        <div class="field">
          <input-number
            data-testid="subaccounts-config-default-due-days-input"
            v-model="formData.default_due_days"
            elem-id="subaccounts-config-default-due-days"
            :label="t('Dias para Vencimento')"
            :min="1"
            :max="90"
          />
          <small class="field-help">{{ t('Número de dias para vencimento padrão das cobranças') }}</small>
        </div>

        <div class="field">
          <input-number
            data-testid="subaccounts-config-default-installments-input"
            v-model="formData.default_installments"
            elem-id="subaccounts-config-default-installments"
            :label="t('Parcelas Padrão')"
            :min="1"
            :max="12"
          />
          <small class="field-help">{{ t('Número padrão de parcelas para cartão de crédito') }}</small>
        </div>
      </div>

      <!-- Notifications Settings -->
      <div class="config-section">
        <h4>{{ t('Notificações') }}</h4>

        <div class="field-checkbox">
          <input-toggle
            data-testid="subaccounts-config-enable-email-toggle"
            v-model="formData.enable_email_notifications"
            elem-id="subaccounts-config-enable-email"
            :label="t('Enviar notificações por Email')"
          />
        </div>

        <div class="field-checkbox">
          <input-toggle
            data-testid="subaccounts-config-enable-sms-toggle"
            v-model="formData.enable_sms_notifications"
            elem-id="subaccounts-config-enable-sms"
            :label="t('Enviar notificações por SMS')"
          />
        </div>

        <div class="field-checkbox">
          <input-toggle
            data-testid="subaccounts-config-enable-whatsapp-toggle"
            v-model="formData.enable_whatsapp_notifications"
            elem-id="subaccounts-config-enable-whatsapp"
            :label="t('Enviar notificações por WhatsApp')"
          />
        </div>
      </div>

      <!-- Automation Settings -->
      <div class="config-section">
        <h4>{{ t('Automação') }}</h4>

        <div class="field-checkbox">
          <input-toggle
            data-testid="subaccounts-config-auto-create-recebimento-toggle"
            v-model="formData.auto_create_recebimento"
            elem-id="subaccounts-config-auto-create-recebimento"
            :label="t('Criar recebimento automaticamente ao confirmar pagamento')"
          />
        </div>

        <div class="field-checkbox">
          <input-toggle
            data-testid="subaccounts-config-sync-customers-toggle"
            v-model="formData.sync_customers"
            elem-id="subaccounts-config-sync-customers"
            :label="t('Sincronizar clientes com o provedor')"
          />
        </div>
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { log } from '@/utils/DebugLogger';

interface ConfigFormData {
  default_payment_method: string;
  default_due_days: number;
  default_installments: number;
  enable_email_notifications: boolean;
  enable_sms_notifications: boolean;
  enable_whatsapp_notifications: boolean;
  auto_create_recebimento: boolean;
  sync_customers: boolean;
}

interface SelectOption {
  ID: string;
  Name: string;
}

interface ConfigDataInput {
  default_payment_method?: string;
  default_due_days?: number;
  default_installments?: number;
  enable_email_notifications?: boolean;
  enable_sms_notifications?: boolean;
  enable_whatsapp_notifications?: boolean;
  auto_create_recebimento?: boolean;
  sync_customers?: boolean;
}

interface Props {
  configData?: ConfigDataInput | null;
}

const props = withDefaults(defineProps<Props>(), {
  configData: null
});

const emit = defineEmits<{
  validate: [isValid: boolean];
}>();

const { t } = useI18n();

// State
const formData = ref<ConfigFormData>({
  default_payment_method: 'any',
  default_due_days: 10,
  default_installments: 1,
  enable_email_notifications: true,
  enable_sms_notifications: false,
  enable_whatsapp_notifications: true,
  auto_create_recebimento: true,
  sync_customers: true
});

// Computed
const paymentMethods = computed<SelectOption[]>(() => {
  return [
    { ID: 'any', Name: t('Qualquer método') },
    { ID: 'boleto', Name: t('Boleto') },
    { ID: 'pix', Name: t('PIX') },
    { ID: 'credit_card', Name: t('Cartão de Crédito') }
  ];
});

// Methods
const loadConfigData = (): void => {
  if (!props.configData) return;

  log('FranchisesSubaccountsConfigForm', 'loadConfigData', 'Loading config data', props.configData);

  formData.value = {
    default_payment_method: props.configData.default_payment_method || 'any',
    default_due_days: props.configData.default_due_days || 10,
    default_installments: props.configData.default_installments || 1,
    enable_email_notifications: props.configData.enable_email_notifications ?? true,
    enable_sms_notifications: props.configData.enable_sms_notifications ?? false,
    enable_whatsapp_notifications: props.configData.enable_whatsapp_notifications ?? true,
    auto_create_recebimento: props.configData.auto_create_recebimento ?? true,
    sync_customers: props.configData.sync_customers ?? true
  };
};

const validate = (): boolean => {
  // Config form is always valid (all fields have defaults)
  return true;
};

const getFormData = (): ConfigFormData => {
  return { ...formData.value };
};

const emitValidation = (): void => {
  // Config form is always valid
  emit('validate', true);
};

// Watch for config data changes from parent
watch(() => props.configData, (newData) => {
  if (newData) {
    loadConfigData();
  }
}, { immediate: true });

// Watch for form changes
watch(formData, () => {
  emitValidation();
}, { deep: true });

// Lifecycle
onMounted(() => {
  log('FranchisesSubaccountsConfigForm', 'onMounted', 'Component mounted');
  emitValidation();
});

// Expose methods for parent
defineExpose({
  validate,
  getFormData
});
</script>

<style scoped>
.payment-subaccount-config-form {
  min-height: 200px;
}

.info-banner {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 1rem;
  background: var(--blue-50);
  border-radius: 6px;
  margin-bottom: 1.5rem;
  color: var(--blue-700);
}

.info-banner i {
  font-size: 1.25rem;
  margin-top: 0.125rem;
}

.config-section {
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: var(--surface-ground);
  border-radius: 6px;
}

.config-section h4 {
  margin: 0 0 1rem 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-color-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.field {
  margin-bottom: 1rem;
}

.field-help {
  display: block;
  color: var(--text-color-secondary);
  font-size: 0.75rem;
  margin-top: 0.25rem;
}

.field-checkbox {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}
</style>
